/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */


import { Component, Input, Output, EventEmitter, ElementRef, Renderer, ViewEncapsulation } from '@angular/core';
import { OnInit, OnDestroy, OnChanges, SimpleChanges, HostBinding, HostListener } from '@angular/core';
import KeyCode from '../util/key.code';
import animation from '../animation/openAnimation';
import scrollIntoView from 'dom-scroll-into-view';
import classNames from 'classnames';

import { MenuItem } from './menu.item';
import { SubMenu } from './submenu';


export interface SelectParam {
    key: string;
    keyPath: string[];
    item: MenuItem | SubMenu;
    domEvent: Event;
    selectedKeys?: string[];
}

export interface ClickParam {
    key: string;
    keyPath: string[];
    item: MenuItem | SubMenu;
    domEvent: Event;
}

export interface OpenParam {
    key: string;
    item: MenuItem | SubMenu;
    trigger?: string;
    open?: boolean;
    originalEvent?: Event;
}

export interface HoverParam {
    key: string;
    item: MenuItem | SubMenu;
    hover?: boolean;
    domEvent: Event;
    trigger: string;
    openChanges?: OpenParam[];
}

export interface OpenChangeParam {
    openKeys: string[];
    menu: Menu;
}
/**
 * @param {?} arr
 * @return {?}
 */
function allDisabled(arr: Array<any>) {
    if (!arr.length) {
        return true;
    }
    return arr.every(c => !!c.props.disabled);
}
/**
 * Menu导航菜单
 * 参考: https://github.com/react-component/menu
 */
export class Menu implements OnInit, OnDestroy, OnChanges {
/**
 * \@internal
 */
private static guid_counter = 0;
/**
 * 样式前缀
 */
prefixCls = 'ant-menu';
/**
 * 菜单类型，现在支持垂直、水平、和内嵌模式三种: `vertical` `horizontal` `inline`。默认为 `vertical`。
 */
mode?: 'vertical' | 'horizontal' | 'inline' = 'vertical';
/**
 * 主题颜色，可选项为`light` `dark`，默认为`light`。
 */
theme = 'light';
/**
 * 展开/关闭菜单时的动画效果 (目前未实现)
 */
openAnimation?: string | Object;
     openTransitionName?: string | Object;
/**
 * 当前选中的菜单项 key 数组
 */
selectedKeys?: Array<string> = [];
/**
 * 当前展开的 SubMenu 菜单项 key 数组
 */
openKeys?: Array<string> = [];
/**
 * 用户CSS样式
 */
class: string;
/**
 * 当前被激活的菜单
 */
activeKey: string;

     defaultActiveFirst = true;

     openSubMenuOnMouseEnter = true;
     closeSubMenuOnMouseLeave = true;

     visible = true;

     selectable = true;

     focusable = true;
/**
 * 是否允许多选，默认为false
 */
multiple?= false;
/**
 * 模式的菜单缩进宽度，默认为24px
 */
inlineIndent = 24;
/**
 * SubMenu 展开/关闭的事件，参数为：({openKeys: string[], menu: Menu})
 */
openChange = new EventEmitter<OpenChangeParam>();
/**
 * menuItem 被选中时的事件，参数为：({ item, key, selectedKeys })
 */
select = new EventEmitter<SelectParam>();
/**
 * menuItem 取消选中时的事件，仅在 multiple 生效，参数为：({ item, key, selectedKeys })
 */
deselect = new EventEmitter<SelectParam>();
/**
 * menuItem 被点击时的事件，参数为：({ item, key, keyPath, domEvent })
 */
menuClick = new EventEmitter<ClickParam>();
/**
 * menuItem 事件，参数为：({ item, key })
 */
menuEnter = new EventEmitter<HoverParam>();
/**
 * menuItem 事件，参数为：({ item, key })
 */
menuLeave = new EventEmitter<HoverParam>();

     role = 'menu';
     activedescendant = '';
public level = 1;

    protected instanceArray: Array<MenuItem> = [];
private switchModeFromInline = false;
/**
 * @param {?} renderer
 * @param {?} elementRef
 */
constructor(private renderer: Renderer,
private elementRef: ElementRef) {

    }
/**
 * 属性：tabIndex
 * @return {?}
 */
get tabIndex(): number {
        return this.focusable ? 0 : null;
    }
/**
 * 菜单样式
 * @return {?}
 */
get classes(): string {
        const /** @type {?} */ classes = classNames(this.prefixCls, {
            [`${this.prefixCls}-${this.theme}`]: this.theme,
            [`${this.prefixCls}-${this.mode}`]: this.mode,
            [`${this.prefixCls}-hidden`]: !this.visible,
            [`${this.prefixCls}-root`]: true
        }, this.class);
        return classes;
    }
/**
 * @param {?} event
 * @return {?}
 */
onKeyDown(event: KeyboardEvent): void {
        if (!this.focusable) {
            return;
        }

        const /** @type {?} */ keyCode = event.keyCode;

        let /** @type {?} */ activeItem: MenuItem = null;
        if (keyCode === KeyCode.UP || keyCode === KeyCode.DOWN) {
            activeItem = this.step(keyCode === KeyCode.UP ? -1 : 1);
        }
        if (activeItem) {
            event.preventDefault();
            this.activeKey = activeItem.eventKey;
            scrollIntoView(activeItem.getHostElement(), this.getHostElement(), {
                onlyScrollIfNeeded: true,
            });
        } else if (activeItem === undefined) {
            event.preventDefault();
            this.activeKey = null;
        }
    }
/**
 * The underlying host native element
 * @return {?}
 */
getHostElement(): HTMLElement {
        return /** @type {?} */(( this.elementRef.nativeElement as HTMLElement));
    }
/**
 * @return {?}
 */
ngOnInit(): void {

    }
/**
 * @return {?}
 */
ngOnDestroy(): void {
        this.instanceArray = [];
    }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges): void {
        const /** @type {?} */ modeChanged = changes['mode'];
        if (modeChanged && modeChanged.previousValue === 'inline') {
            this.switchModeFromInline = true;
        }
    }
/**
 * 当要销毁子菜单/子菜单项时
 * 
 * @param {?} key 菜单项的Key
 * @return {?}
 */
onDestroy(key: string): void {
        let /** @type {?} */ index = this.selectedKeys.indexOf(key);
        if (index !== -1) {
            this.selectedKeys.splice(index, 1);
        }
        index = this.openKeys.indexOf(key);
        if (index !== -1) {
            this.openKeys.splice(index, 1);
        }
    }
/**
 * @param {?} info
 * @return {?}
 */
onClick(info: ClickParam): void {
        // There is `this.openKeys` for closing vertical popup submenu after click it
        if (this.mode !== 'inline') {
            this.openKeys = [];
        }
        this.menuClick.emit(info);
    }
/**
 * @param {?} info
 * @return {?}
 */
onMouseEnter(info: HoverParam) {
        this.menuEnter.emit(info);
    }
/**
 * @param {?} info
 * @return {?}
 */
onMouseLeave(info: HoverParam) {
        this.menuLeave.emit(info);
    }
/**
 * @param {?} e
 * @return {?}
 */
onItemHover(e: HoverParam): void {
        const /** @type {?} */ item: MenuItem | SubMenu = e.item;
        let { openChanges = [] } = e;

        // special for top sub menu
        if (this.mode !== 'inline' && !this.closeSubMenuOnMouseLeave && item.isSubMenu) {
            const /** @type {?} */ activeKey = this.activeKey;
            const /** @type {?} */ activeItem = this.instanceArray.filter((c) => {
                return c && c.eventKey === activeKey;
            })[0];
            console.log('item === activeItem ?===>' + (item === activeItem));
            if (activeItem && activeItem.open) {
                openChanges = openChanges.concat({
                    key: item.eventKey,
                    item: item,
                    originalEvent: e.domEvent,
                    open: true
                });
            }
        }
        openChanges = openChanges.concat(this.getOpenChangesOnItemHover(e));
        if (openChanges.length) {
            this.onOpenChange(openChanges);
        }
    }
/**
 * @param {?} e_
 * @return {?}
 */
onOpenChange(e_: OpenParam | OpenParam[]): void {
        const /** @type {?} */ openKeys = this.openKeys.concat();
        let /** @type {?} */ changed = false;
        const /** @type {?} */ processSingle = (e: OpenParam) => {
            let /** @type {?} */ oneChanged = false;
            if (e.open) {
                oneChanged = openKeys.indexOf(e.key) === -1;
                if (oneChanged) {
                    openKeys.push(e.key);
                }
            } else {
                const /** @type {?} */ index = openKeys.indexOf(e.key);
                oneChanged = index !== -1;
                if (oneChanged) {
                    openKeys.splice(index, 1);
                }
            }
            changed = changed || oneChanged;
        };

        if (Array.isArray(e_)) {
            e_.forEach(processSingle); // batch change call
        } else {
            processSingle(e_);
        }

        if (changed) {
            this.openKeys = openKeys;
            this.openChange.emit({openKeys, menu: this}); // emit events
        }
    }
/**
 * @param {?} selectInfo
 * @return {?}
 */
onSelect(selectInfo: SelectParam): void {
        if (this.selectable) {
            // root menu
            let /** @type {?} */ selectedKeys = this.selectedKeys;
            const /** @type {?} */ selectedKey = selectInfo.key;
            if (this.multiple) {
                selectedKeys = selectedKeys.concat([selectedKey]);
            } else {
                selectedKeys = [selectedKey];
            }
            this.selectedKeys = selectedKeys;

            // emit events
            this.select.emit(selectInfo);
        }
    }
/**
 * @param {?} selectInfo
 * @return {?}
 */
onDeselect(selectInfo: SelectParam): void {
        if (this.selectable) {
            const /** @type {?} */ selectedKeys = this.selectedKeys.concat();
            const /** @type {?} */ selectedKey = selectInfo.key;
            const /** @type {?} */ index = selectedKeys.indexOf(selectedKey);
            if (index !== -1) {
                selectedKeys.splice(index, 1);
            }
            this.selectedKeys = selectedKeys;

            // emit events
            this.deselect.emit(selectInfo);
        }
    }
/**
 * @return {?}
 */
getOpenAnimationName(): string | Object {
        let /** @type {?} */ openAnimation = this.openAnimation || this.openTransitionName;
        if (this.openAnimation === undefined && this.openTransitionName === undefined) {
            switch (this.mode) {
                case 'horizontal':
                    openAnimation = 'slide-up';
                    break;
                case 'vertical':
                    // When mode switch from inline
                    // submenu should hide without animation
                    if (this.switchModeFromInline) {
                        openAnimation = '';
                        this.switchModeFromInline = false;
                    } else {
                        openAnimation = 'zoom-big';
                    }
                    break;
                case 'inline':
                    openAnimation = animation;
                    break;
                default:
            }
        }
        return openAnimation;
    }
/**
 * @return {?}
 */
isInlineMode(): boolean {
        return this.mode === 'inline';
    }
/**
 * @return {?}
 */
lastOpenSubMenu(): MenuItem {
        let /** @type {?} */ lastOpen: MenuItem[] = [];
        if (this.openKeys.length) {
            lastOpen = this.instanceArray.filter((c) => {
                return c && this.openKeys.indexOf(c.eventKey) !== -1;
            });
        }
        return lastOpen[0];
    }
/**
 * @param {?} direction
 * @return {?}
 */
step(direction: number): MenuItem {
        let /** @type {?} */ children = this.instanceArray;
        const /** @type {?} */ activeKey = this.activeKey;
        const /** @type {?} */ len = children.length;
        if (!len) {
            return null;
        }
        if (direction < 0) {
            children = children.concat().reverse();
        }

        // find current activeIndex
        let /** @type {?} */ activeIndex = -1;
        children.every((c, ci) => {
            if (c && c.eventKey === activeKey) {
                activeIndex = ci;
                return false;
            }
            return true;
        });
        if (!this.defaultActiveFirst && activeIndex !== -1) {
            if (allDisabled(children.slice(activeIndex, len - 1))) {
                return undefined;
            }
        }

        const /** @type {?} */ start = (activeIndex + 1) % len;
        let /** @type {?} */ i = start;
        while (true) {
            const /** @type {?} */ child = children[i];
            if (!child || child.disabled) {
                i = (i + 1 + len) % len;
                // complete a loop
                if (i === start) {
                    return null;
                }
            } else {
                return child;
            }
        }
    }
/**
 * @param {?} e
 * @return {?}
 */
getOpenChangesOnItemHover(e: HoverParam): OpenParam | OpenParam[] {
        const /** @type {?} */ mode = this.mode;
        const { key, hover, trigger } = e;
        const /** @type {?} */ activeKey = this.activeKey;
        if (!trigger || hover ||
            this.closeSubMenuOnMouseLeave || !e.item.isSubMenu || mode === 'inline') {
            this.activeKey = hover ? key : null; // 当前激活的菜单
        } else {
            // keep active for sub menu for click active
            // empty
        }

        // clear last open status
        if (hover && mode !== 'inline') {
            const /** @type {?} */ activeItem = this.instanceArray.filter((c) => {
                return c && c.eventKey === activeKey;
            })[0];
            if (activeItem && activeItem.isSubMenu && activeItem.eventKey !== key) {
                return {
                    item: activeItem,
                    originalEvent: e.domEvent,
                    key: activeItem.eventKey,
                    open: false,
                };
            }
        }
        return [];
    }
/**
 * @param {?} item
 * @return {?}
 */
registerItem(item: MenuItem): void {
        this.instanceArray.push(item);
    }
/**
 * @param {?} prefix
 * @return {?}
 */
getGUID(prefix: string): string {
        return `${prefix}-item-${Menu.guid_counter++}`;
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'ant-menu',
    template: '<ng-content></ng-content>',
    styleUrls: ['./style/index.scss'],
    encapsulation: ViewEncapsulation.None
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'prefixCls': [{ type: Input },],
'mode': [{ type: Input },],
'theme': [{ type: Input },],
'openAnimation': [{ type: Input },],
'openTransitionName': [{ type: Input },],
'selectedKeys': [{ type: Input },],
'openKeys': [{ type: Input },],
'class': [{ type: Input },],
'activeKey': [{ type: Input },],
'defaultActiveFirst': [{ type: Input },],
'openSubMenuOnMouseEnter': [{ type: Input },],
'closeSubMenuOnMouseLeave': [{ type: Input },],
'visible': [{ type: Input },],
'selectable': [{ type: Input },],
'focusable': [{ type: Input },],
'multiple': [{ type: Input },],
'inlineIndent': [{ type: Input },],
'openChange': [{ type: Output },],
'select': [{ type: Output },],
'deselect': [{ type: Output },],
'menuClick': [{ type: Output },],
'menuEnter': [{ type: Output },],
'menuLeave': [{ type: Output },],
'role': [{ type: HostBinding, args: ['attr.role', ] },],
'activedescendant': [{ type: HostBinding, args: ['attr.aria-activedescendant', ] },],
'tabIndex': [{ type: HostBinding, args: ['attr.tabIndex', ] },],
'classes': [{ type: HostBinding, args: ['class', ] },],
'onKeyDown': [{ type: HostListener, args: ['keydown', ['$event'], ] },],
};
}

function Menu_tsickle_Closure_declarations() {
/**
 * \@internal
 * @type {?}
 */
Menu.guid_counter;
/** @type {?} */
Menu.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Menu.ctorParameters;
/** @type {?} */
Menu.propDecorators;
/**
 * 样式前缀
 * @type {?}
 */
Menu.prototype.prefixCls;
/**
 * 菜单类型，现在支持垂直、水平、和内嵌模式三种: `vertical` `horizontal` `inline`。默认为 `vertical`。
 * @type {?}
 */
Menu.prototype.mode;
/**
 * 主题颜色，可选项为`light` `dark`，默认为`light`。
 * @type {?}
 */
Menu.prototype.theme;
/**
 * 展开/关闭菜单时的动画效果 (目前未实现)
 * @type {?}
 */
Menu.prototype.openAnimation;
/** @type {?} */
Menu.prototype.openTransitionName;
/**
 * 当前选中的菜单项 key 数组
 * @type {?}
 */
Menu.prototype.selectedKeys;
/**
 * 当前展开的 SubMenu 菜单项 key 数组
 * @type {?}
 */
Menu.prototype.openKeys;
/**
 * 用户CSS样式
 * @type {?}
 */
Menu.prototype.class;
/**
 * 当前被激活的菜单
 * @type {?}
 */
Menu.prototype.activeKey;
/** @type {?} */
Menu.prototype.defaultActiveFirst;
/** @type {?} */
Menu.prototype.openSubMenuOnMouseEnter;
/** @type {?} */
Menu.prototype.closeSubMenuOnMouseLeave;
/** @type {?} */
Menu.prototype.visible;
/** @type {?} */
Menu.prototype.selectable;
/** @type {?} */
Menu.prototype.focusable;
/**
 * 是否允许多选，默认为false
 * @type {?}
 */
Menu.prototype.multiple;
/**
 * 模式的菜单缩进宽度，默认为24px
 * @type {?}
 */
Menu.prototype.inlineIndent;
/**
 * SubMenu 展开/关闭的事件，参数为：({openKeys: string[], menu: Menu})
 * @type {?}
 */
Menu.prototype.openChange;
/**
 * menuItem 被选中时的事件，参数为：({ item, key, selectedKeys })
 * @type {?}
 */
Menu.prototype.select;
/**
 * menuItem 取消选中时的事件，仅在 multiple 生效，参数为：({ item, key, selectedKeys })
 * @type {?}
 */
Menu.prototype.deselect;
/**
 * menuItem 被点击时的事件，参数为：({ item, key, keyPath, domEvent })
 * @type {?}
 */
Menu.prototype.menuClick;
/**
 * menuItem 事件，参数为：({ item, key })
 * @type {?}
 */
Menu.prototype.menuEnter;
/**
 * menuItem 事件，参数为：({ item, key })
 * @type {?}
 */
Menu.prototype.menuLeave;
/** @type {?} */
Menu.prototype.role;
/** @type {?} */
Menu.prototype.activedescendant;
/** @type {?} */
Menu.prototype.level;
/** @type {?} */
Menu.prototype.instanceArray;
/** @type {?} */
Menu.prototype.switchModeFromInline;
/** @type {?} */
Menu.prototype.renderer;
/** @type {?} */
Menu.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
